import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import type { ConsultationFieldDefinition } from '../types/Consultation';
import type { StructuredConsultationField } from '../consultationUtils';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import styles from './ConsultationAddFieldPanel.module.css';

const FIELD_DEFINITIONS_PATH = '/consultations/field-definitions';

type ConsultationAddFieldPanelProps = {
  disabled?: boolean;
  fieldDefinitions: ConsultationFieldDefinition[];
  fields: StructuredConsultationField[];
  onAdd: (fieldDefinitionId: string, isRequired: boolean) => void | Promise<void>;
  saving?: boolean;
  fieldDefinitionsReturnTo?: string;
};

const SettingsIcon: React.FC = () => (
  <svg className={styles.settingsIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
      stroke="currentColor"
      strokeWidth="1.75"
    />
    <path
      d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.26.604.852.997 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ConsultationAddFieldPanel: React.FC<ConsultationAddFieldPanelProps> = ({
  disabled = false,
  fieldDefinitions,
  fields,
  onAdd,
  saving = false,
  fieldDefinitionsReturnTo,
}) => {
  const { t } = useTranslation(['consultations', 'common']);
  const { hasPermission } = useAuth();
  const canManageFieldDefinitions = hasPermission(PermissionScopes.consultationsManage);
  const location = useLocation();
  const returnTo = fieldDefinitionsReturnTo ?? `${location.pathname}${location.search}`;
  const [fieldDefinitionId, setFieldDefinitionId] = React.useState('');
  const [isRequired, setIsRequired] = React.useState(false);

  const assignedFieldIds = React.useMemo(() => new Set(fields.map((field) => field.id)), [fields]);
  const availableDefinitions = React.useMemo(
    () => fieldDefinitions
      .filter((definition) => definition.isActive && !assignedFieldIds.has(definition.id))
      .sort((left, right) => left.name.localeCompare(right.name)),
    [assignedFieldIds, fieldDefinitions],
  );

  React.useEffect(() => {
    if (fieldDefinitionId && !availableDefinitions.some((definition) => definition.id === fieldDefinitionId)) {
      setFieldDefinitionId('');
    }
  }, [availableDefinitions, fieldDefinitionId]);

  const handleAdd = React.useCallback(async () => {
    if (!fieldDefinitionId) {
      return;
    }

    await onAdd(fieldDefinitionId, isRequired);
    setFieldDefinitionId('');
    setIsRequired(false);
  }, [fieldDefinitionId, isRequired, onAdd]);

  if (disabled) {
    return null;
  }

  return (
    <div className={styles.panel}>
      <h3 className={styles.title}>{t('consultationAddFieldTitle', { defaultValue: 'Add field to this consultation' })}</h3>
      <p className={styles.hint}>
        {t('consultationAddFieldHint', {
          defaultValue: 'Choose an existing field definition to include on this consultation only. Use the settings icon to create a new field definition if it is not listed.',
        })}
      </p>
      <div className={styles.controls}>
        <div className={styles.selectRow}>
          <select
            className="text-input"
            value={fieldDefinitionId}
            onChange={(event) => setFieldDefinitionId(event.target.value)}
            disabled={saving || availableDefinitions.length === 0}
          >
            <option value="">
              {availableDefinitions.length === 0
                ? t('consultationNoAvailableFields', { defaultValue: 'No more fields available' })
                : t('consultationSelectFieldDefinition', { defaultValue: 'Select field definition' })}
            </option>
            {availableDefinitions.map((definition) => (
              <option key={definition.id} value={definition.id}>{definition.name}</option>
            ))}
          </select>
          {canManageFieldDefinitions ? (
            <Link
              to={FIELD_DEFINITIONS_PATH}
              state={{ returnTo }}
              className={styles.settingsLink}
              title={t('manageFieldDefinitions', { defaultValue: 'Manage field definitions' })}
              aria-label={t('manageFieldDefinitions', { defaultValue: 'Manage field definitions' })}
            >
              <SettingsIcon />
            </Link>
          ) : null}
        </div>
        <label className={styles.requiredToggle}>
          <input
            type="checkbox"
            checked={isRequired}
            onChange={(event) => setIsRequired(event.target.checked)}
            disabled={saving || !fieldDefinitionId}
          />
          {t('consultationFieldRequiredForConsultation', { defaultValue: 'Required for this consultation' })}
        </label>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => void handleAdd()}
          disabled={saving || !fieldDefinitionId}
        >
          {saving
            ? t('adding', { ns: 'common', defaultValue: 'Adding…' })
            : t('consultationAddField', { defaultValue: 'Add field' })}
        </button>
      </div>
    </div>
  );
};
